import { Component, Input, Injector, OnInit, Output, EventEmitter } from '@angular/core';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { CustomerObjectServiceCallDto } from '../../../shared/models/customer-object-service-calls/customer-object-service-call.dto';
import { CustomerObjectServiceCallsService } from '../customer-object-service-calls.service';
import { Request } from "../../../../app/shared/models/request/request";
import { SecurityService } from '../../../core/services/security.service';
import { RequestStatus, RequestType } from '../../../shared/enums';
import { RequestsService } from '../../../shared/services/requests.service';
import { Agreement } from '../../../shared/models/agreement/agreement';
import { AgreementsService } from '../../../shared/services/agreements.service';
import { CustomerObjectServiceCallCommentsComponent } from '../customer-object-service-call-comments/customer-object-service-call-comments.component';
import { forkJoin } from 'rxjs';
import { LookupService } from '../../../shared/services/lookup.service';
import { NotificationService } from '../../../core/services/notification.service';
import { CommentKindEnum } from '../../../shared/enums/comment-kind.enum';
import { CommentTypeEnum } from '../../../shared/enums/comment-type.enum';


@Component({
	selector: 'customer-object-service-call',
	templateUrl: './customer-object-service-call.component.html',
	styleUrls: ['./customer-object-service-call.component.scss',
		'../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
		'../../../../vendor/libs/angular2-ladda/angular2-ladda.scss'
	]
})
export class CustomerObjectServiceCallComponent implements OnInit {

	@Input() customerObjectId: number;

	@Output() refreshCallsEvent: EventEmitter<any> = new EventEmitter<any>();

	item: CustomerObjectServiceCallDto;

	createServiceRequestLoading: boolean = false;
	createAgreementLoading: boolean = false;
	commentSending: boolean = false;

	private readonly serviceMaintenanceInitTypeCode = 'ServiceMaintenance';
	private readonly createdFromServiceMaintenanceRegistryComment = 'Заявка создана из реестра сервисного обслуживания.';

	constructor(
		private injector: Injector,
		private dialogService: DialogService,
		private customerObjectServiceCallsService: CustomerObjectServiceCallsService,
		private securityService: SecurityService,
		private requestsService: RequestsService,
		private agreementsService: AgreementsService,
		private lookupService: LookupService,
		private notificationService: NotificationService
	) { }

	ngOnInit(): void {
		this.loadCustomerObjectServiceCall();
	}

	onCommentAdded(): void {
		this.loadCustomerObjectServiceCall();
	}

	loadCustomerObjectServiceCall() {
		this.customerObjectServiceCallsService.getById(this.customerObjectId).subscribe(resp => {
			this.item = resp.entity as CustomerObjectServiceCallDto;
			if (this.item.postponeServiceCallDateTime != null) {
				this.item.postponeServiceCallDateTime = new Date(this.item.postponeServiceCallDateTime);
			}
		});
	}

	close() {
		this.injector.get(DialogRef).close();
	}

	postpone(): void {
		const postponeDialogRef = this.dialogService.open({
			content: CustomerObjectServiceCallCommentsComponent,
			title: 'Отложить — добавить комментарий',
			width: '700px'
		});

		const commentsInstance = postponeDialogRef.content.instance as CustomerObjectServiceCallCommentsComponent;
		commentsInstance.customerObjectId = this.item.customerObjectId;
		commentsInstance.comments = this.item.comments;
		commentsInstance.commentPrefix = "Отложено: ";
		commentsInstance.serviceCallFinalised = true;
		commentsInstance.showPostponeFields = true;
		commentsInstance.postponeForever = this.item.postponeForever;
		if (this.item.postponeServiceCallDateTime) {
			commentsInstance.postponeServiceCallDateTime = new Date(this.item.postponeServiceCallDateTime);
		} else if (!this.item.postponeForever) {
			const defaultPostponeDate = new Date();
			defaultPostponeDate.setMonth(defaultPostponeDate.getMonth() + 6);
			commentsInstance.postponeServiceCallDateTime = defaultPostponeDate;
		} else {
			commentsInstance.postponeServiceCallDateTime = null;
		}

		commentsInstance.onCommentAdded.subscribe(() => {
			postponeDialogRef.close();
			this.refreshCallsEvent.emit();
			this.close();
		});
	}

	createServiceRequest() {
		this.createServiceRequestLoading = true;

		this.lookupService.getDataForCombobox('init-types', null).subscribe(initTypes => {
			const serviceMaintenance = (initTypes || []).find(x => x.integraCode === this.serviceMaintenanceInitTypeCode);
			if (!serviceMaintenance) {
				this.createServiceRequestLoading = false;
				this.notificationService.error({
					title: 'Ошибка',
					message: 'Не найден источник «Сервисное обслуживание» (Code = ServiceMaintenance).'
				});
				return;
			}

			let newRequest = new Request;
			newRequest.customerContragentId = this.item.contragentId;
			newRequest.customerObjectId = this.item.customerObjectId;
			newRequest.serviceCenterId = this.item.serviceCenterId;
			newRequest.performerUserId = this.securityService._currentUser.userId;
			newRequest.statusId = <number>RequestStatus.InProgress;
			newRequest.agreementId = this.item.agreementId;
			newRequest.requestTypeId = this.item.needToServiceAgreementRequest
				? <number>RequestType.serviceAgreement
				: <number>RequestType.service;
			newRequest.initType = serviceMaintenance.id;
			newRequest.requestComments = [{
				initType: serviceMaintenance.id,
				commentType: CommentTypeEnum.user,
				commentKind: CommentKindEnum.system,
				text: this.createdFromServiceMaintenanceRegistryComment,
				attachments: []
			}];

			this.requestsService.create(newRequest).subscribe((resp) => {
				if (!resp || !resp.isSuccessful) {
					this.createServiceRequestLoading = false;
					return;
				}

				forkJoin([
					this.customerObjectServiceCallsService.createComment(this.customerObjectId, 'Создана заявка на обслуживание', true),
					this.customerObjectServiceCallsService.performServiceCall(this.customerObjectId, false)
				]).subscribe(() => {
					this.createServiceRequestLoading = false;
					this.refreshCallsEvent.emit();
					this.close();
					window.open(`/requests/${resp.data}`, '_blank');
				}, () => this.createServiceRequestLoading = false);
			}, () => this.createServiceRequestLoading = false);
		}, () => this.createServiceRequestLoading = false);
	}

	createAgreement() {
		this.createAgreementLoading = true;
		let agreement = new Agreement;
		agreement.customerObjectId = this.item.customerObjectId;
		agreement.contragentId = this.item.contragentId;
		
		this.agreementsService.create(agreement).subscribe(resp => {
			window.open(`/categories/agreement/${resp.data}?step=step1`, '_blank');
			this.customerObjectServiceCallsService.createComment(this.customerObjectId, 'Создан договор на обслуживание', true).subscribe(() => {
				this.createAgreementLoading = false;
				this.refreshCallsEvent.emit();
				this.close();
			}, () => this.createAgreementLoading = false);
		}, () => this.createAgreementLoading = false);
	}
}
